export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
      <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Legal</div>
      <h1 className="mt-1 text-4xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Terms of Service</h1>
      <p className="mt-3 text-sm text-zinc-400">Last updated: 30 April 2026</p>

      <div className="mt-10 space-y-8 text-zinc-300 leading-relaxed text-sm">

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">1. Acceptance of Terms</h2>
          <p>By accessing or using HumanizeAI at <strong>academichumanize.com</strong> ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">2. Description of Service</h2>
          <p>HumanizeAI provides AI-powered text analysis and paraphrasing tools designed to help users understand and modify AI-generated content. The Service includes AI detection scoring, text paraphrasing, document management, and related features.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">3. Acceptable Use</h2>
          <p className="mb-2">You agree to use the Service only for lawful purposes. You must not use the Service to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Submit content that is illegal, harmful, defamatory, or infringes on the intellectual property rights of others.</li>
            <li>Engage in academic dishonesty, plagiarism, or submit AI-generated work as your own in violation of your institution's academic integrity policies.</li>
            <li>Attempt to circumvent, disable, or interfere with security features of the Service.</li>
            <li>Scrape, harvest, or extract data from the Service by automated means without our written permission.</li>
            <li>Use the Service in a way that could damage, disable, overburden, or impair our servers or networks.</li>
            <li>Resell or redistribute the Service without prior written consent.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">4. Subscription Plans</h2>
          <p className="mb-2">The Service is offered on the following plans:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Free</strong> — Limited to 500 words per document, 1 scan per day, 1 paraphrase round. Shows 3 most recent documents.</li>
            <li><strong>Pro</strong> — Up to 10,000 words, unlimited daily scans, unlimited rounds, full document history.</li>
            <li><strong>Business</strong> — Up to 50,000 words, batch processing up to 10 documents, all Pro features.</li>
          </ul>
          <p className="mt-2">Plan limits may be updated from time to time. Current limits are shown in the admin settings and on the Pricing page.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">5. Payments and Billing</h2>
          <p>Payments are processed by our third-party payment providers. By subscribing to a paid plan, you authorise us to charge the applicable fee. All prices are displayed on the Pricing page. Subscriptions are for a period of 30 days from the date of payment. We reserve the right to change pricing with reasonable notice.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">6. Refund Policy</h2>
          <p>Refunds are considered on a case-by-case basis. If you experience a significant technical failure that prevents you from using the Service during your subscription period, contact us at <a href="mailto:privacy@academichumanize.com" className="text-indigo-400 hover:underline">privacy@academichumanize.com</a> within 7 days of payment. We do not offer refunds for change-of-mind purchases or partial use of the subscription period.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">7. AI-Generated Content Disclaimer</h2>
          <p className="mb-2">You acknowledge and agree that:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>AI detection scores are estimates and may not be 100% accurate. No AI detection tool guarantees perfect results.</li>
            <li>Paraphrased output is generated by AI models and may contain errors, inaccuracies, or altered meaning. You are responsible for reviewing all output before use.</li>
            <li>HumanizeAI is a writing assistance tool. We make no warranty that paraphrased content will pass any specific AI detection system.</li>
            <li>You are solely responsible for how you use the Service and any content you produce with it. HumanizeAI is not liable for any consequences arising from misuse of the Service.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">8. Intellectual Property</h2>
          <p>The Service, including its design, code, and branding, is owned by HumanizeAI and protected by intellectual property laws. You retain ownership of all content you upload. By uploading content, you grant us a limited licence to process it for the purpose of providing the Service.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">9. Account Termination</h2>
          <p>We reserve the right to suspend or terminate your account at our discretion if you violate these Terms, engage in fraudulent activity, or abuse the Service. You may delete your account at any time from your Profile page. Upon termination, your data will be deleted as described in our Privacy Policy.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">10. Limitation of Liability</h2>
          <p>To the maximum extent permitted by applicable law, HumanizeAI shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability to you for any claim shall not exceed the amount you paid to us in the 30 days preceding the claim.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">11. Governing Law</h2>
          <p>These Terms are governed by and construed in accordance with the laws of the <strong>Republic of South Africa</strong>. Any disputes shall be subject to the exclusive jurisdiction of the courts of South Africa.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">12. Changes to Terms</h2>
          <p>We may update these Terms from time to time. We will notify you of significant changes by updating the "Last updated" date above. Continued use of the Service after changes constitutes acceptance of the revised Terms.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">13. Contact</h2>
          <p>For any questions about these Terms, contact us at <a href="mailto:privacy@academichumanize.com" className="text-indigo-400 hover:underline">privacy@academichumanize.com</a>.</p>
        </section>

      </div>
    </div>
  );
}
